import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";

// 2026-10-21: filters (the same labels as /stats and broadcasts) and granting a plan for 7 / 14 / 30 days.
const PLAN_NAMES = ["START", "PRO", "EXPERT"];
const DAYS = [7, 14, 30];
const PAGE = 50;

function fmtDate(v?: string | null) {
  if (!v) return "";
  const d = new Date(v);
  return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", timeZone: "Europe/Moscow" });
}

function labelBadge(label?: string | null) {
  if (!label) return { text: "—", bg: "#9ca3af" };
  if (label === "ADMIN") return { text: "Админ", bg: "#6b7280" };
  if (label === "TRIAL") return { text: "Пробный", bg: "#f59e0b" };
  if (label === "NONE") return { text: "Без подписки", bg: "#9ca3af" };
  if (label.endsWith("*")) return { text: label.slice(0, -1) + " · выдан", bg: "#8b5cf6" };
  return { text: label + " · оплачен", bg: "#16a34a" };
}

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<any[]>([]);
  const [plans, setPlans] = useState<any[]>([]);
  const [filters, setFilters] = useState<any[]>([]);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [openGrant, setOpenGrant] = useState<number | null>(null);
  const [grantPlan, setGrantPlan] = useState("PRO");
  const [grantDays, setGrantDays] = useState(7);
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState<number | null>(null);
  const [note, setNote] = useState<{ id: number; text: string } | null>(null);
  const timer = useRef<any>(null);

  const loadUsers = () => {
    setLoading(true);
    const q = query ? `&q=${encodeURIComponent(query)}` : "";
    const f = filter !== "all" ? `&f=${filter}` : "";
    api.get(`/admin/users?page=${page}&page_size=${PAGE}${q}${f}`).then((r: any) => {
      setUsers(r.data.items);
      setTotal(r.data.total);
    }).finally(() => setLoading(false));
  };

  const loadFilters = () => {
    api.get("/admin/user-filters").then((r: any) => setFilters(r.data)).catch(() => {});
  };

  useEffect(() => { loadUsers(); }, [page, query, filter]);
  useEffect(() => {
    loadFilters();
    api.get("/admin/plans").then((r: any) => setPlans(r.data));
  }, []);

  const onInput = (v: string) => {
    setInput(v);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { setPage(1); setQuery(v.trim()); }, 400);
  };

  const pickFilter = (key: string) => { setPage(1); setFilter(key); setOpenGrant(null); };

  const toggleBlock = async (id: number, current: boolean) => {
    await api.post(`/admin/users/${id}/block`, { is_blocked: !current });
    setUsers(users.map((u) => (u.id === id ? { ...u, is_blocked: !current } : u)));
  };

  const grant = async (u: any) => {
    const plan = plans.find((p) => p.name === grantPlan);
    if (!plan) return;
    setBusy(true);
    try {
      const r: any = await api.post(`/admin/users/${u.id}/subscription`, { plan_id: plan.id, days: grantDays, notify });
      const d = r.data || {};
      const parts = [`✅ ${grantPlan} ${d.extended ? "продлён" : "выдан"} до ${fmtDate(d.expires_at)}`];
      if (d.resumed) parts.push(`поисков возобновлено: ${d.resumed}`);
      if (notify) parts.push(d.notified ? "сообщение отправлено" : "сообщение не дошло");
      setNote({ id: u.id, text: parts.join(" · ") });
      setOpenGrant(null);
      loadUsers();
      loadFilters();
    } catch (e: any) {
      setNote({ id: u.id, text: "Ошибка: " + (e.response?.data?.detail || "не удалось") });
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (u: any) => {
    if (confirmCancel !== u.id) { setConfirmCancel(u.id); return; }
    setConfirmCancel(null);
    try {
      await api.post(`/admin/users/${u.id}/subscription`, { plan_id: null, days: 30 });
      setNote({ id: u.id, text: "Подписка аннулирована" });
      loadUsers();
      loadFilters();
    } catch (e: any) {
      setNote({ id: u.id, text: "Ошибка: " + (e.response?.data?.detail || "не удалось") });
    }
  };

  const hasPlan = (u: any) => u.label && u.label !== "NONE" && u.label !== "TRIAL" && u.label !== "ADMIN";
  const pages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <div style={styles.container}>
      <button onClick={() => navigate("/admin")} style={styles.back}>← Админ-панель</button>
      <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
        <button onClick={() => navigate("/admin/growth")} style={{ ...styles.tab, backgroundColor: "var(--tg-theme-secondary-bg-color, #fff)", color: "var(--tg-theme-text-color, #000)" }}>‹ Динамика</button>
        <button style={{ ...styles.tab, backgroundColor: "var(--tg-theme-button-color, #2481cc)", color: "var(--tg-theme-button-text-color, #fff)" }}>Список</button>
      </div>
      <h1 style={styles.title}>Пользователи</h1>

      <div style={styles.chips}>
        {filters.map((f) => {
          const on = f.key === filter;
          return (
            <button key={f.key} onClick={() => pickFilter(f.key)} style={{ ...styles.chip, backgroundColor: on ? "var(--tg-theme-button-color, #2481cc)" : "var(--tg-theme-secondary-bg-color, #fff)", color: on ? "var(--tg-theme-button-text-color, #fff)" : "var(--tg-theme-text-color, #000)" }}>
              {f.title} <span style={{ opacity: 0.7 }}>{f.count}</span>
            </button>
          );
        })}
      </div>

      <input type="text" placeholder="🔍 Имя, @username или TG ID" value={input} onChange={(e) => onInput(e.target.value)} style={styles.searchInput} />
      <div style={styles.found}>{loading ? "Загрузка…" : `Найдено: ${total}`}</div>

      <div style={styles.list}>
        {users.map((u) => {
          const b = labelBadge(u.label);
          const until = hasPlan(u) ? u.plan_expires_at : u.label === "TRIAL" ? u.trial_expires_at : null;
          return (
            <div key={u.id} style={styles.card}>
              <div style={styles.cardTop}>
                <div style={{ minWidth: 0 }}>
                  <a href={u.username ? `https://t.me/${u.username}` : `tg://user?id=${u.telegram_id}`} target="_blank" rel="noopener noreferrer" style={styles.userNameLink}>
                    {u.first_name || "—"} {u.last_name || ""} (@{u.username || "—"})
                  </a>
                  <div style={styles.badges}>
                    <span style={{ ...styles.badge, backgroundColor: b.bg }}>{b.text}</span>
                    {until && <span style={styles.hintInline}>до {fmtDate(until)}</span>}
                    {u.bot_blocked && <span style={{ ...styles.badge, backgroundColor: "#dc2626" }}>заблокировал бота</span>}
                  </div>
                  <div style={styles.userHint}>
                    TG: {u.telegram_id} · поиски: {u.active_searches_count || 0} акт. из {u.searches_total || 0} · с {fmtDate(u.created_at)}{u.source ? ` · метка ${u.source}` : ""}
                  </div>
                </div>
                <button onClick={() => toggleBlock(u.id, u.is_blocked)} style={{ ...styles.blockBtn, backgroundColor: u.is_blocked ? "#16a34a" : "#dc2626" }}>
                  {u.is_blocked ? "Разблок." : "Блок."}
                </button>
              </div>

              <div style={styles.planRow}>
                <button onClick={() => navigate(`/admin/users/${u.id}/searches`)} style={styles.searchesBtn}>🔍 Поиски</button>
                <button onClick={() => { setOpenGrant(openGrant === u.id ? null : u.id); setNote(null); }} style={styles.planBtn}>🎁 Выдать доступ</button>
                {hasPlan(u) && (
                  <button onClick={() => cancel(u)} style={{ ...styles.cancelBtn, backgroundColor: confirmCancel === u.id ? "#dc2626" : "#6b7280" }}>
                    {confirmCancel === u.id ? "Точно аннулировать?" : "Аннулировать"}
                  </button>
                )}
              </div>

              {openGrant === u.id && (
                <div style={styles.grantBox}>
                  <div style={styles.grantLabel}>Тариф</div>
                  <div style={styles.optRow}>
                    {PLAN_NAMES.filter((n) => plans.some((p) => p.name === n)).map((n) => (
                      <button key={n} onClick={() => setGrantPlan(n)} style={{ ...styles.opt, ...(grantPlan === n ? styles.optOn : {}) }}>{n}</button>
                    ))}
                  </div>
                  <div style={styles.grantLabel}>Срок</div>
                  <div style={styles.optRow}>
                    {DAYS.map((d) => (
                      <button key={d} onClick={() => setGrantDays(d)} style={{ ...styles.opt, ...(grantDays === d ? styles.optOn : {}) }}>{d} дней</button>
                    ))}
                  </div>
                  <label style={styles.check}>
                    <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} /> Сообщить пользователю в боте
                  </label>
                  <div style={styles.grantHint}>Если этот тариф уже действует, срок прибавится к текущему.</div>
                  <button disabled={busy} onClick={() => grant(u)} style={styles.grantBtn}>
                    {busy ? "Выдаю…" : `Выдать ${grantPlan} на ${grantDays} дней`}
                  </button>
                </div>
              )}
              {note && note.id === u.id && <div style={styles.note}>{note.text}</div>}
            </div>
          );
        })}
        {!loading && users.length === 0 && <div style={styles.empty}>Никого не нашлось</div>}
      </div>

      <div style={styles.pagination}>
        <button style={styles.pageBtn} disabled={page <= 1} onClick={() => setPage(page - 1)}>← Назад</button>
        <span style={styles.pageInfo}>{page} / {pages}</span>
        <button style={styles.pageBtn} disabled={page >= pages} onClick={() => setPage(page + 1)}>Вперёд →</button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: "16px", maxWidth: "480px", margin: "0 auto" },
  back: { background: "none", border: "none", color: "var(--tg-theme-link-color, #2481cc)", fontSize: "14px", cursor: "pointer", padding: 0, marginBottom: "12px" },
  tab: { flex: 1, padding: "8px", borderRadius: "10px", border: "none", fontSize: "13px", cursor: "pointer" },
  title: { fontSize: "22px", fontWeight: 700, margin: "0 0 12px 0", color: "var(--tg-theme-text-color, #000)" },
  chips: { display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "12px" },
  chip: { padding: "6px 10px", borderRadius: "16px", border: "1px solid var(--tg-theme-hint-color, #e0e0e0)", fontSize: "12px", cursor: "pointer" },
  searchInput: { width: "100%", boxSizing: "border-box", padding: "10px 14px", borderRadius: "12px", border: "1px solid var(--tg-theme-hint-color, #e0e0e0)", backgroundColor: "var(--tg-theme-secondary-bg-color, #fff)", color: "var(--tg-theme-text-color, #000)", fontSize: "14px", marginBottom: "8px", outline: "none" },
  found: { fontSize: "12px", color: "var(--tg-theme-hint-color, #666)", marginBottom: "10px" },
  list: { display: "flex", flexDirection: "column", gap: "10px" },
  card: { backgroundColor: "var(--tg-theme-secondary-bg-color, #fff)", borderRadius: "14px", padding: "12px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" },
  cardTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px", marginBottom: "10px" },
  userNameLink: { fontSize: "14px", fontWeight: 600, color: "var(--tg-theme-link-color, #2481cc)", textDecoration: "none", display: "block", wordBreak: "break-word" },
  badges: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px", marginTop: "4px" },
  badge: { padding: "2px 8px", borderRadius: "8px", fontSize: "11px", color: "#fff" },
  hintInline: { fontSize: "11px", color: "var(--tg-theme-hint-color, #666)" },
  userHint: { fontSize: "11px", color: "var(--tg-theme-hint-color, #666)", marginTop: "4px" },
  blockBtn: { padding: "4px 10px", borderRadius: "8px", fontSize: "12px", color: "#fff", border: "none", cursor: "pointer", flexShrink: 0 },
  planRow: { display: "flex", gap: "6px", flexWrap: "wrap" },
  searchesBtn: { padding: "6px 10px", borderRadius: "8px", fontSize: "12px", backgroundColor: "#8b5cf6", color: "#fff", border: "none", cursor: "pointer" },
  planBtn: { padding: "6px 10px", borderRadius: "8px", fontSize: "12px", backgroundColor: "var(--tg-theme-button-color, #2481cc)", color: "var(--tg-theme-button-text-color, #fff)", border: "none", cursor: "pointer" },
  cancelBtn: { padding: "6px 10px", borderRadius: "8px", fontSize: "12px", color: "#fff", border: "none", cursor: "pointer" },
  grantBox: { marginTop: "10px", padding: "10px", borderRadius: "12px", border: "1px solid var(--tg-theme-hint-color, #e0e0e0)" },
  grantLabel: { fontSize: "12px", color: "var(--tg-theme-hint-color, #666)", marginBottom: "6px" },
  optRow: { display: "flex", gap: "6px", marginBottom: "10px" },
  opt: { flex: 1, padding: "8px 0", borderRadius: "8px", fontSize: "13px", border: "1px solid var(--tg-theme-hint-color, #e0e0e0)", background: "none", color: "var(--tg-theme-text-color, #000)", cursor: "pointer" },
  optOn: { backgroundColor: "var(--tg-theme-button-color, #2481cc)", color: "var(--tg-theme-button-text-color, #fff)", border: "1px solid var(--tg-theme-button-color, #2481cc)" },
  check: { display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "var(--tg-theme-text-color, #000)", marginBottom: "6px" },
  grantHint: { fontSize: "11px", color: "var(--tg-theme-hint-color, #666)", marginBottom: "10px" },
  grantBtn: { width: "100%", padding: "10px", borderRadius: "10px", border: "none", fontSize: "14px", fontWeight: 600, backgroundColor: "#16a34a", color: "#fff", cursor: "pointer" },
  note: { marginTop: "8px", fontSize: "12px", color: "var(--tg-theme-text-color, #000)" },
  empty: { textAlign: "center", fontSize: "13px", color: "var(--tg-theme-hint-color, #666)", padding: "20px 0" },
  pagination: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "14px", fontSize: "13px", color: "var(--tg-theme-text-color, #000)" },
  pageBtn: { background: "none", border: "none", color: "var(--tg-theme-link-color, #2481cc)", cursor: "pointer", fontSize: "13px" },
  pageInfo: { color: "var(--tg-theme-hint-color, #666)" },
};
